import React, { memo } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { lineAccountIdState } from "../../../store/fetchedData/lineAccountIdState";
import { CircularLoading } from "../../molecules/loading/CircularLoading";
import { useSwrNoAuth } from "../../../hooks/swr/useSwrNoAuth";
import { Box } from "@chakra-ui/react";
import { usePostOverallScoreForm } from "../../../hooks/usePostOverallScoreForm";
import { PostOverallScoreForm } from "../../molecules/form/PostOverallScoreForm";
import { customNoAuthAxios } from "../../../utils/customNoAuthAxios";

type PostSoCouponOverallScoreProps = {
  restaurantId: string;
};

type SoCouponSetting = {
  id: string;
  restaurant: string;
  branch_score: number;
  g_url: string;
  default_url?: string; // 追加（未設定の店舗もあるため optional）
  socoupon_in_use: string;
  socoupon_title: string;
};

export const PostSoCouponOverallScore: React.FC<PostSoCouponOverallScoreProps> = memo(({ restaurantId }) => {
  const lineAccountId = useRecoilValue(lineAccountIdState);

  const {
    data: overallScore,
    isLoading: isLoadingOverallScore,
    error: overallScoreError,
  } = useSwrNoAuth<number | null>(`socoupon/customers/${lineAccountId}/overall-score/`);

  const {
    data: soCouponSetting,
    isLoading: isLoadingSoCouponSetting,
  } = useSwrNoAuth<SoCouponSetting>(`socoupon/restaurants/${restaurantId}/setting/customer/`);

  // 404 は「未回答」扱い。それ以外のエラーは必要に応じて別途ハンドリング
  const isNotAnswered = overallScoreError?.response?.status === 404 || overallScore == null;

  if (isLoadingOverallScore || isLoadingSoCouponSetting || !soCouponSetting) {
    return <CircularLoading />;
  }

  // 回答済みはアプリ内ルートへ（Unauthorized を避けるため LIFF URL 取得は行わない）
  if (!isNotAnswered) {
    return <Navigate replace to={`/customers/${lineAccountId}/socoupon`} />;
  }

  // 未回答は総合スコア投稿フォーム（default_url に対応）
  return (
    <Box p={4}>
      <ScorePost
        lineAccountId={lineAccountId!}
        restaurantId={restaurantId}
        branchScore={soCouponSetting.branch_score}
        gUrl={soCouponSetting.g_url}
        defaultUrl={soCouponSetting.default_url ?? '/thanks'}
      />
    </Box>
  );
});

const ScorePost: React.FC<{
  lineAccountId: string;
  restaurantId: string;
  branchScore: number;
  gUrl: string;
  defaultUrl: string; // 常に string を受け取る（フォールバック済み）
}> = memo(({ lineAccountId, restaurantId, branchScore, gUrl, defaultUrl }) => {
  const { handleSubmit, control, isSubmitting, errors } = usePostOverallScoreForm();
  const navigate = useNavigate();

  const onSubmit = async (data: any) => {
    try {
      const scoreNum = Number(data.score);

      // 総合スコアの登録
      await customNoAuthAxios.post(
        `socoupon/customers/${lineAccountId}/overall-score/`,
        { restaurant_id: restaurantId, score: scoreNum },
      );

      // スコアが閾値以上なら（＝良い評価なら）追加アンケートをスキップして g_url へ
      if (Number.isFinite(scoreNum) && scoreNum >= branchScore) {
        try {
          const u = new URL(gUrl);
          if (u.protocol === 'http:' || u.protocol === 'https:') {
            window.location.href = gUrl;
            return;
          }
        } catch {
          // not an absolute URL, fall through to internal navigate
        }
        // 内部パスの場合
        navigate(gUrl, { replace: true });
        return;
      }

      // 閾値未満は追加アンケートへ。アンケート完了後の遷移先（nextUrl）は defaultUrl（未設定なら /thanks）
      const nextUrl = defaultUrl;
      try {
        sessionStorage.setItem("socoupon:nextUrl", nextUrl);
      } catch (_) {
        // noop
      }

      navigate(`/socoupon/survey/${restaurantId}`, {
        replace: true,
        state: { nextUrl },
      });
    } catch (e) {
      // 必要に応じてエラーハンドリングを追加
    }
  };

  return (
    <PostOverallScoreForm
      onSubmit={handleSubmit(onSubmit)}
      control={control}
      isSubmitting={isSubmitting}
      errors={errors}
    />
  );
});